import type { FrontendChatMessage } from "./messages/types";
import type {
  AgentQuestion,
  AgentQuestionAnswer,
  AnalysisChatV2Message,
} from "@/backend/db/drizzle/types/jsonTypes";
import {
  type PromptAnswerText,
  SKIPPED_PROMPT_ANSWER,
  formatPromptAnswers,
  formatSelectedOptionLabels,
} from "@/components/ui/chat-composer/promptAnswers";
import { assertNever } from "@/packages/ts-utils";
import { notNull } from "@/utils/helpers";
import type { FrontendChatMessageId } from "@/utils/types";

export const AGENT_QUESTION_CUSTOM_ANSWER_MAX_LENGTH = 2000;

export type AgentQuestionAnswerItem = PromptAnswerText;

const getAnswerItem = ({
  question,
  answer,
}: {
  question: AgentQuestion;
  answer: AgentQuestionAnswer;
}): AgentQuestionAnswerItem | null => {
  switch (answer.type) {
    case "options": {
      const selectedOptions = question.options.filter((option) =>
        answer.selectedOptionIds.includes(option.id)
      );
      const hasUnknownOrRepeatedOption =
        selectedOptions.length !== answer.selectedOptionIds.length;
      const hasTooManyOptions =
        !question.isMultiSelect && selectedOptions.length > 1;
      if (hasUnknownOrRepeatedOption || hasTooManyOptions) return null;
      return {
        question: question.text,
        answer: formatSelectedOptionLabels(
          selectedOptions.map((option) => option.label)
        ),
      };
    }
    case "custom":
      return { question: question.text, answer: answer.text };
    case "skipped":
      return { question: question.text, answer: SKIPPED_PROMPT_ANSWER };
    default:
      return assertNever(answer);
  }
};

/** Null unless the answers cover each question exactly once with options it offers. */
export const getAgentQuestionAnswerItems = ({
  questions,
  answers,
}: {
  questions: AgentQuestion[];
  answers: AgentQuestionAnswer[];
}): AgentQuestionAnswerItem[] | null => {
  if (questions.length === 0 || answers.length !== questions.length) {
    return null;
  }
  const items = questions
    .map((question) => {
      const answersToQuestion = answers.filter(
        (answer) => answer.questionId === question.id
      );
      return answersToQuestion.length === 1
        ? getAnswerItem({ question, answer: answersToQuestion[0] })
        : null;
    })
    .filter(notNull);
  return items.length === questions.length ? items : null;
};

export type AnswerRejectionReason = "no_pending_questions" | "mismatch";

export type UserMessageContent = {
  text: string;
  agentQuestionAnswers: AgentQuestionAnswer[] | undefined;
  answerRejectionReason: AnswerRejectionReason | undefined;
};

/** Valid answers replace the client's text with the server's wording; invalid ones are dropped and the text kept. */
export const getUserMessageContent = ({
  previousMessages,
  userMessage,
  agentQuestionAnswers,
}: {
  previousMessages: AnalysisChatV2Message[];
  userMessage: string;
  agentQuestionAnswers: AgentQuestionAnswer[] | undefined;
}): UserMessageContent => {
  if (!agentQuestionAnswers) {
    return {
      text: userMessage,
      agentQuestionAnswers: undefined,
      answerRejectionReason: undefined,
    };
  }
  const latestMessage = previousMessages.at(-1);
  const questions =
    latestMessage?.role === "assistant"
      ? (latestMessage.agentQuestions ?? [])
      : [];
  const items = getAgentQuestionAnswerItems({
    questions,
    answers: agentQuestionAnswers,
  });
  if (!items) {
    return {
      text: userMessage,
      agentQuestionAnswers: undefined,
      answerRejectionReason:
        questions.length === 0 ? "no_pending_questions" : "mismatch",
    };
  }
  return {
    text: formatPromptAnswers(items),
    agentQuestionAnswers,
    answerRejectionReason: undefined,
  };
};

/** Questions from the latest assistant message, until the user replies to them. */
export const getPendingAgentQuestions = <T extends FrontendChatMessageId>(
  messages: FrontendChatMessage<T>[]
): AgentQuestion[] | null => {
  const lastMessage = messages.at(-1);
  if (lastMessage?.role !== "assistant") return null;
  const questions = lastMessage.agentQuestions ?? [];
  return questions.length > 0 ? questions : null;
};
